/* eslint-disable */
/**
 * 全局统一的密码复杂度评估（本地规则，不依赖第三方词表库）
 */

/** 当前登录用户相关字符串（改密时排除与账号相关的弱口令） */
export function getDefaultPasswordUserInputs () {
  const userInputs = []
  try {
    const personal = JSON.parse(localStorage.personal || '{}')
    if (personal && personal.userName) userInputs.push(String(personal.userName))
    if (personal && personal.nickName) userInputs.push(String(personal.nickName))
    if (personal && personal.phonenumber) userInputs.push(String(personal.phonenumber))
  } catch (e) {}
  if (localStorage.loginTissueName) userInputs.push(String(localStorage.loginTissueName))
  return userInputs
}

function countCharTypes (pwd) {
  const s = String(pwd || '')
  let n = 0
  if (/\d/.test(s)) n += 1
  if (/[a-zA-Z]/.test(s)) n += 1
  if (/[^a-zA-Z0-9]/.test(s)) n += 1
  return n
}

function containsUserRelated (pwd, userInputs) {
  const lower = String(pwd || '').toLowerCase()
  if (!lower) return false
  return (userInputs || []).some((item) => {
    const t = String(item || '').trim().toLowerCase()
    return t.length >= 3 && lower.indexOf(t) !== -1
  })
}

/**
 * @param {string} pwd
 * @param {{ userInputs?: string[] }} [options]
 *   - 不传 userInputs：使用当前登录用户信息（修改自己的密码）
 *   - 传入 userInputs：仅使用这些作为字典（如后台新建其它用户时传入被创建人的账号/手机/昵称）
 * @returns {{ score: number, percent: number, text: string, className: string, tip: string }}
 *   className: 'none' | 'weak' | 'medium' | 'strong'
 */
export function evaluatePasswordStrength (pwd, options = {}) {
  const value = String(pwd || '')
  if (!value) {
    return { score: 0, percent: 0, text: '未输入', className: 'none', tip: '' }
  }
  const userInputs = Array.isArray(options.userInputs)
    ? options.userInputs.filter(Boolean).map(String)
    : getDefaultPasswordUserInputs()

  const lenOk = isPasswordLengthValid(value)
  const types = countCharTypes(value)
  const typesOk = types >= 2
  const seqBad = hasForbiddenRepeatOrSequence(value)
  const personalBad = containsUserRelated(value, userInputs)

  if (!lenOk || !typesOk || seqBad || personalBad) {
    let tip = '密码强度不足，请加长并提高复杂度。'
    if (!lenOk) tip = '长度需为 6～16 位。'
    else if (!typesOk) tip = '请至少使用数字、字母、符号中的两种。'
    else if (seqBad) tip = '请勿使用连续或重复 6 位以上的字符。'
    else if (personalBad) tip = '请勿使用易关联个人信息的内容。'
    return { score: 1, percent: 33, text: '弱', className: 'weak', tip }
  }

  if (value.length >= 10 && types >= 3) {
    return { score: 3, percent: 100, text: '强', className: 'strong', tip: '密码强度良好。' }
  }
  if (value.length >= 8 && types >= 2) {
    return { score: 2, percent: 66, text: '中', className: 'medium', tip: '强度一般，可适当加长或增加字符类型。' }
  }
  return { score: 1, percent: 33, text: '弱', className: 'weak', tip: '密码强度不足，请加长并提高复杂度。' }
}

/** 修改登录密码时，弱密码拦截提示（全系统统一口径） */
export const MESSAGE_REJECT_WEAK_PASSWORD_ON_CHANGE =
  '新密码强度不足，请修改后重新提交。'

/** 新建或维护用户账号时，弱密码拦截提示（全系统统一口径） */
export const MESSAGE_REJECT_WEAK_PASSWORD_ON_USER_SAVE =
  '登录密码强度不足，请修改后重新提交。'

/** 不符合 6～16 位及字符类型、连续/重复规则时（与弹窗内校验一致） */
export const MESSAGE_PASSWORD_POLICY_REJECT =
  '密码不符合格式要求，请按规则修改后重新提交。'

/** 是否低于可接受强度（与改密、建用户等处的「弱」拦截一致） */
export function isPasswordStrengthWeak (result) {
  return !!(result && result.className === 'weak')
}

/** ---------- 登录密码格式规则（与改密弹窗展示一致，6～16 位） ---------- */

export const PASSWORD_LEN_MIN = 6
export const PASSWORD_LEN_MAX = 16

export const PASSWORD_POLICY_TEXT = {
  length: '密码由 6-16 位组成',
  types: '至少含有 2 种以上数字、字母或符号',
  seq: '请勿输入连续、重复 6 位以上字母或数字，如 abcdef、111111、0123456'
}

export function isPasswordLengthValid (pwd) {
  const len = String(pwd || '').length
  return len >= PASSWORD_LEN_MIN && len <= PASSWORD_LEN_MAX
}

/** 数字、字母、符号 三类中至少命中两类 */
export function isPasswordTwoPlusCharTypes (pwd) {
  const s = String(pwd || '')
  if (!s) return false
  let n = 0
  if (/\d/.test(s)) n += 1
  if (/[a-zA-Z]/.test(s)) n += 1
  if (/[^a-zA-Z0-9]/.test(s)) n += 1
  return n >= 2
}

function isSixMonotonicSameKind (slice) {
  if (slice.length !== 6) return false
  const allDigit = /^\d{6}$/.test(slice)
  const allLower = /^[a-z]{6}$/.test(slice)
  const allUpper = /^[A-Z]{6}$/.test(slice)
  if (!(allDigit || allLower || allUpper)) return false
  const codes = [...slice].map(c => c.charCodeAt(0))
  const inc = codes.every((c, i) => i === 0 || c === codes[i - 1] + 1)
  const dec = codes.every((c, i) => i === 0 || c === codes[i - 1] - 1)
  return inc || dec
}

/** 同一字符连续 6 位及以上，或纯数字/纯同大小写字母且步长为 1 的连续 6 位 */
export function hasForbiddenRepeatOrSequence (pwd) {
  const s = String(pwd || '')
  if (!s) return false
  if (/(.)\1{5,}/.test(s)) return true
  for (let i = 0; i <= s.length - 6; i += 1) {
    if (isSixMonotonicSameKind(s.slice(i, i + 6))) return true
  }
  return false
}

export function isPasswordPolicyValid (pwd) {
  const raw = String(pwd || '')
  return isPasswordLengthValid(raw) && isPasswordTwoPlusCharTypes(raw) && !hasForbiddenRepeatOrSequence(raw)
}

/**
 * 用于改密等界面逐条展示：ok / fail / pending（未输入时为 pending）
 * 第三条（连续/重复）：仅在已输入且触发违规时出现，避免常态占位打扰。
 * @returns {{ key: string, text: string, status: 'ok'|'fail'|'pending' }[]}
 */
export function getPasswordPolicyRuleStates (pwd) {
  const raw = String(pwd || '')
  const empty = raw.length === 0
  const okLen = isPasswordLengthValid(raw)
  const okTypes = isPasswordTwoPlusCharTypes(raw)
  const badSeq = hasForbiddenRepeatOrSequence(raw)

  const lenStatus = empty ? 'pending' : (okLen ? 'ok' : 'fail')
  const typesStatus = empty ? 'pending' : (okTypes ? 'ok' : 'fail')

  const rows = [
    { key: 'length', text: PASSWORD_POLICY_TEXT.length, status: lenStatus },
    { key: 'types', text: PASSWORD_POLICY_TEXT.types, status: typesStatus }
  ]
  if (!empty && badSeq) {
    rows.push({ key: 'seq', text: PASSWORD_POLICY_TEXT.seq, status: 'fail' })
  }
  return rows
}



// WEBPACK FOOTER //
// ./src/utils/passwordStrength.js